import React, { useState, useEffect } from 'react';
import { Synth } from 'tone';
import './MusicQuizCard.css';
import DraggableSymbols from './DraggableSymbols';
import MusicStaffDropZone from './MusicStaffDropZone';
import crossIcon from "../../../../assets/wrong-icon.png";
import checkIcon from "../../../../assets/check-icon.png";

// Piano component for rendering the piano keyboard
const Piano = ({ 
  highlightedNote = null, 
  onNoteClick = () => {}, 
  showFeedback = false,
  correctNote = null,
  selectedNote = null,
  pianoFromNote = null,
  pianoToNote = null,
  isDisabled = false
}) => {
  const [synth, setSynth] = useState(null);
  const [whiteKeys, setWhiteKeys] = useState([]);
  
  // Parse note to get note name and octave
  const parseNote = (note) => {
    if (!note) return { noteName: 'C', octave: 4 };
    const match = note.match(/^([A-G]#?)(\d+)$/);
    if (match) {
      return { noteName: match[1], octave: parseInt(match[2]) };
    }
    return { noteName: 'C', octave: 4 };
  };
  
  // Get all notes between pianoFromNote and pianoToNote
  const generateNotes = () => {
    if (!pianoFromNote && !pianoToNote) {
      // Default to one octave if no range specified
      const allNotes = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
      return allNotes.map(note => `${note}4`);
    }
    
    const fromNote = parseNote(pianoFromNote);
    const toNote = parseNote(pianoToNote);
    
    const allNotes = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
    const notes = [];
    
    // If both notes are the same, show one octave around that note
    if (pianoFromNote === pianoToNote) {
      const noteIndex = allNotes.indexOf(fromNote.noteName);
      const startIndex = Math.max(0, noteIndex - 2);
      const endIndex = Math.min(6, noteIndex + 2);
      
      for (let i = startIndex; i <= endIndex; i++) {
        notes.push(`${allNotes[i]}${fromNote.octave}`);
      }
      
      // Add next octave if needed
      if (endIndex === 6) {
        notes.push(`C${fromNote.octave + 1}`);
      }
    } else {
      // Handle range between two different notes
      const fromIndex = allNotes.indexOf(fromNote.noteName);
      const toIndex = allNotes.indexOf(toNote.noteName);
      
      if (fromNote.octave === toNote.octave) {
        // Same octave
        const startIndex = Math.min(fromIndex, toIndex);
        const endIndex = Math.max(fromIndex, toIndex);
        
        for (let i = startIndex; i <= endIndex; i++) {
          notes.push(`${allNotes[i]}${fromNote.octave}`);
        }
      } else {
        // Different octaves
        if (fromNote.octave < toNote.octave) {
          // Ascending range
          // Add notes from fromNote to end of its octave
          for (let i = fromIndex; i < allNotes.length; i++) {
            notes.push(`${allNotes[i]}${fromNote.octave}`);
          }
          
          // Add full octaves in between
          for (let octave = fromNote.octave + 1; octave < toNote.octave; octave++) {
            allNotes.forEach(note => notes.push(`${note}${octave}`));
          }
          
          // Add notes from start of toNote's octave to toNote
          for (let i = 0; i <= toIndex; i++) {
            notes.push(`${allNotes[i]}${toNote.octave}`);
          }
        } else {
          // Descending range
          // Add notes from fromNote to start of its octave
          for (let i = fromIndex; i >= 0; i--) {
            notes.push(`${allNotes[i]}${fromNote.octave}`);
          }
          
          // Add full octaves in between
          for (let octave = fromNote.octave - 1; octave > toNote.octave; octave--) {
            allNotes.forEach(note => notes.push(`${note}${octave}`));
          }
          
          // Add notes from end of toNote's octave to toNote
          for (let i = allNotes.length - 1; i >= toIndex; i--) {
            notes.push(`${allNotes[i]}${toNote.octave}`);
          }
        }
      }
    }
    
    return notes;
  };
  
  const notes = generateNotes();
  
  // Black keys map: which white key they appear after
  const blackKeyMap = {
    'C': 'C#',
    'D': 'D#',
    'F': 'F#',
    'G': 'G#',
    'A': 'A#'
  };
  
  // Piano layout mapping for proper black key positioning
  const pianoLayout = {
    'C': { hasBlackKey: true, blackKeyPosition: 'right' },
    'D': { hasBlackKey: true, blackKeyPosition: 'right' },
    'E': { hasBlackKey: false },
    'F': { hasBlackKey: true, blackKeyPosition: 'right' },
    'G': { hasBlackKey: true, blackKeyPosition: 'right' },
    'A': { hasBlackKey: true, blackKeyPosition: 'right' },
    'B': { hasBlackKey: false }
  };
  
  // Standard piano key positions (C, C#, D, D#, E, F, F#, G, G#, A, A#, B)
  const standardPianoKeys = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  
  useEffect(() => {
    // Initialize synth
    const newSynth = new Synth();
    newSynth.toDestination();
    setSynth(newSynth);
    
      // Generate white keys with proper black key mapping
  const keys = notes.map((note, index) => {
    const noteName = note.slice(0, 1); // Get the note letter (C, D, etc.)
    const layout = pianoLayout[noteName] || { hasBlackKey: false };
    
    // Calculate the position of this white key in the piano layout
    const whiteKeyPosition = index * 50; // Each white key is 50px wide
    
    return {
      note: note,
      hasBlackKey: layout.hasBlackKey,
      blackKeyPosition: layout.blackKeyPosition,
      whiteKeyPosition: whiteKeyPosition
    };
  });
    
    setWhiteKeys(keys);
    
    return () => {
      newSynth.dispose();
    };
  }, [pianoFromNote, pianoToNote]);
  
  const handleKeyClick = (note) => {
    if (synth && !isDisabled) {
      synth.triggerAttackRelease(note, "8n");
      onNoteClick(note);
    }
  };
  
  const getKeyClass = (note) => {
    let className = '';
    
    if (note === highlightedNote) {
      className += ' highlighted';
    }
    
    if (showFeedback) {
      if (note === selectedNote && note === correctNote) {
        className += ' correct';
      } else if (note === selectedNote && note !== correctNote) {
        className += ' incorrect';
      }
    }
    
    if (isDisabled) {
      className += ' disabled';
    }
    
    return className;
  };
  
  return (
    <div className="piano">
      {whiteKeys.map((key, i) => (
        <div 
          key={`white-${key.note}`}
          className={`white-key${getKeyClass(key.note)}`}
          style={{
            left: `${key.whiteKeyPosition}px`,
            cursor: isDisabled ? 'default' : 'pointer'
          }}
          onClick={() => handleKeyClick(key.note)}
          data-note={key.note}
        />
      ))}
      
      {whiteKeys.map((key, i) => {
        if (key.hasBlackKey) {
          const noteName = key.note.slice(0, 1);
          const octave = key.note.slice(1);
          const blackNote = `${blackKeyMap[noteName]}${octave}`;
          
          // Calculate black key position based on the white key position
          const whiteKeyWidth = 50; // Width of each white key
          const blackKeyWidth = -15; // Width of black key
          
          // Position black key between the current white key and the next white key
          const blackKeyOffset = (whiteKeyWidth - blackKeyWidth) / 2; // Center black key over the gap
          const blackKeyPosition = key.whiteKeyPosition + blackKeyOffset;
          
          return (
            <div 
              key={`black-${blackNote}`}
              className={`black-key${getKeyClass(blackNote)}`}
              style={{ 
                left: `${blackKeyPosition}px`,
                top: 0,
                cursor: isDisabled ? 'default' : 'pointer'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleKeyClick(blackNote);
              }}
              data-note={blackNote}
            />
          );
        }
        return null;
      })}
      
      {/* Middle C label */}
      {whiteKeys.map((key, i) => {
        if (key.note === 'C4') {
          return (
            <div 
              key={`middle-c-label-${key.note}`}
              className="middle-c-label"
              style={{
                fontSize: 10,
                position: 'absolute',
                left: `${key.whiteKeyPosition + 25}px`, // Center the label on the C4 key
                top: '190px', // Position below the piano keys
                transform: 'translateX(-50%)' // Center the label
              }}
            >
              Middle C
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

// Staff component for rendering music notation
const StaffRenderer = ({ width = 400, height = 150 }) => {
  return (
    <div 
      className="staff-area"
      style={{
        width: `${width}px`,
        height: `${height}px`
      }}
    >
      {/* Simplified treble clef */}
      <div style={{ 
        position: 'absolute', 
        left: '10px', 
        top: '20px', 
        fontSize: '60px', 
        lineHeight: '0'
      }}>
        𝄞
      </div>
      
      {/* Note placeholder - will be positioned based on the note */}
      <div 
        className="quiz-note" 
        style={{
          display: 'none' // Hidden by default
        }}
      />
    </div>
  );
};

// Main MusicQuizCard component
const MusicQuizCard = ({ quiz, idx, onCorrectAnswer }) => {
  const [selectedNote, setSelectedNote] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [textAnswer, setTextAnswer] = useState('');
  const [isCorrect, setIsCorrect] = useState(false);
  const [isAnswered, setIsAnswered] = useState(false);
  const [selectedHand, setSelectedHand] = useState('right'); // 'right' for treble, 'left' for bass
  const [startTime] = useState(Date.now()); // Track when the question was first shown
  
  // Map notes to staff positions (y-coordinate)
  const notePositions = {
    'C3': 95, 'D3': 90, 'E3': 85, 'F3': 80, 'G3': 75, 'A3': 70, 'B3': 65,
    'C4': 60, 'D4': 55, 'E4': 50, 'F4': 45, 'G4': 40, 'A4': 35, 'B4': 30,
    'C5': 25, 'D5': 20, 'E5': 15, 'F5': 10, 'G5': 5
  };
  
  // Extract note name without octave (e.g., C4 -> C)
  const getSimpleNoteName = (note) => {
    if (!note) return '';
    return note.replace(/[0-9]/g, '');
  };
  
  // Handle note click on piano
  const handleNoteClick = (note) => {
    // Only allow one answer - if already answered, ignore subsequent clicks
    if (isAnswered) {
      return;
    }
    
    setSelectedNote(note);
    setShowFeedback(true);
    
    // Check if the answer is correct
    const isNoteCorrect = note === quiz.correctAnswer;
    setIsCorrect(isNoteCorrect);
    setIsAnswered(true);
    
    // Calculate answer time in seconds
    const answerTimeSeconds = Math.round((Date.now() - startTime) / 1000);
    
    if (isNoteCorrect) {
      setResultMessage('✅ Correct!');
      onCorrectAnswer(idx, note, answerTimeSeconds, isNoteCorrect);
    } else {
      setResultMessage(`❌ Wrong! The correct answer is ${quiz.correctAnswer}.`);
      onCorrectAnswer(idx, note, answerTimeSeconds, isNoteCorrect);
    }
  };
  
  // Handle text input for note identification
  const handleTextInputChange = (e) => {
    setTextAnswer(e.target.value);
  };
  
  // Handle text answer submission
  const handleTextSubmit = () => {
    setIsAnswered(true);
    
    // Check if the answer is correct (case insensitive)
    const userAnswer = textAnswer.trim().toUpperCase();
    const correctAnswer = getSimpleNoteName(quiz.correctAnswer).toUpperCase();
    const isTextCorrect = userAnswer === correctAnswer;
    
    setIsCorrect(isTextCorrect);
    
    // Calculate answer time in seconds
    const answerTimeSeconds = Math.round((Date.now() - startTime) / 1000);
    
    if (isTextCorrect) {
      setResultMessage('✅ Correct!');
      onCorrectAnswer(idx, userAnswer, answerTimeSeconds, isTextCorrect);
    } else {
      setResultMessage(`❌ Wrong! The correct answer is ${correctAnswer}.`);
      onCorrectAnswer(idx, userAnswer, answerTimeSeconds, isTextCorrect);
    }
  };
  
  // Render different question types
  const renderQuestionContent = () => {
    switch (quiz.questionType) {
      case 'TEXT_NOTE_TO_PIANO':
        // Play a specific note on the piano
        return (
          <div>
            {/* <p>{quiz.questionText}</p> */}
            <Piano 
              onNoteClick={handleNoteClick}
              showFeedback={showFeedback}
              selectedNote={selectedNote}
              correctNote={quiz.correctAnswer}
              pianoFromNote={quiz.pianoFromNote}
              pianoToNote={quiz.pianoToNote}
              isDisabled={isAnswered}
            />
          </div>
        );
        
      case 'PIANO_TO_TEXT_NOTE':
        // Identify a highlighted note on the piano
        return (
          <div>
           {/* <p>{quiz.questionText}</p> */}
            <Piano 
              highlightedNote={quiz.highlightedKey}
              pianoFromNote={quiz.pianoFromNote}
              pianoToNote={quiz.pianoToNote}
              isDisabled={isAnswered}
            />
            <div style={{ marginTop: '20px' }}>
              <input
                type="text"
                value={textAnswer}
                onChange={handleTextInputChange}
                disabled={isAnswered}
                placeholder="Enter note name (e.g., C, D#)"
                className="note-input"
              />
              {!isAnswered && (
                <button 
                  onClick={handleTextSubmit}
                  className="submit-button"
                >
                  Submit
                </button>
              )}
            </div>
          </div>
        );
        
      case 'SYMBOL_NOTE_TO_PIANO':
        // Play a note represented by a symbol
        return (
          <div>
            {/* <div style={{ fontSize: '48px', margin: '20px 0' }}>
              {quiz.pianoFromNote}
            </div> */}
            {/* <p>Click on the piano key that corresponds to this symbol.</p> */}
            <Piano 
              onNoteClick={handleNoteClick}
              showFeedback={showFeedback}
              selectedNote={selectedNote}
              correctNote={quiz.correctAnswer}
              pianoFromNote={quiz.pianoFromNote}
              pianoToNote={quiz.pianoToNote}
              isDisabled={isAnswered}
            />
          </div>
        );
        
      case 'PIANO_TO_SYMBOL_NOTE':
        // Drag and drop implementation for symbols with ghost positions
        return (
          <div>
            <div style={{ marginBottom: '15px', textAlign: 'center', fontSize: '16px', color: '#666' }}>
              Drag the correct musical symbol to the staff position that matches the highlighted piano key
            </div>
            {/* Hand Toggle */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              marginBottom: '20px',
              gap: '10px'
            }}>
              <span style={{ fontSize: '14px', color: '#666' }}>Staff:</span>
              <div style={{
                display: 'flex',
                backgroundColor: '#f8f9fa',
                borderRadius: '20px',
                padding: '2px',
                border: '1px solid #dee2e6'
              }}>
                <button
                  onClick={() => setSelectedHand('right')}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '18px',
                    border: 'none',
                    backgroundColor: selectedHand === 'right' ? '#007bff' : 'transparent',
                    color: selectedHand === 'right' ? 'white' : '#666',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Treble clef
                </button>
                <button
                  onClick={() => setSelectedHand('left')}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '18px',
                    border: 'none',
                    backgroundColor: selectedHand === 'left' ? '#007bff' : 'transparent',
                    color: selectedHand === 'left' ? 'white' : '#666',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Bass clef
                </button>
              </div>
            </div>
            <Piano 
              highlightedNote={quiz.highlightedKey}
              pianoFromNote={quiz.pianoFromNote}
              pianoToNote={quiz.pianoToNote}
              isDisabled={isAnswered}
            />
            <div className="staff-container" style={{ marginTop: '20px' }}>
              <MusicStaffDropZone 
                clef={selectedHand === 'right' ? 'treble' : 'bass'}
                onDrop={(symbol, position) => {
                  setIsAnswered(true);

                  const vexKeyToNote = (vexKey) => {
                    if (!vexKey || typeof vexKey !== 'string') return null;
                    const parts = vexKey.split('/');
                    if (parts.length !== 2) return null;
                    const [letter, octave] = parts;
                    return `${letter.toUpperCase()}${octave}`;
                  };

                  const droppedNote = vexKeyToNote(position && position.vexKey);

                  // Check if the dropped symbol is correct
                  let isSymbolCorrect = false;
                  let feedbackMessage = '';

                  if (droppedNote && quiz.highlightedKey) {
                    // Check if the pitch matches the highlighted key
                    isSymbolCorrect = droppedNote === quiz.highlightedKey;
                    feedbackMessage = isSymbolCorrect 
                      ? `✅ Correct! You placed ${symbol.name} at the right position (${droppedNote})`
                      : `❌ Wrong! You placed ${symbol.name} at ${droppedNote}, but the correct position is ${quiz.highlightedKey}`;
                  } else {
                    // Fallback to symbol type check if pitch comparison isn't available
                    isSymbolCorrect = symbol.id === quiz.correctAnswer || symbol.label === quiz.correctAnswer;
                    feedbackMessage = isSymbolCorrect 
                      ? `✅ Correct! You selected the right symbol: ${symbol.name}`
                      : `❌ Wrong! The correct symbol should be ${quiz.correctAnswer}`;
                  }

                  setIsCorrect(isSymbolCorrect);
                  setResultMessage(feedbackMessage);
                  
                  // Calculate answer time in seconds
                  const answerTimeSeconds = Math.round((Date.now() - startTime) / 1000);
                  
                  // Call the callback with the result
                  onCorrectAnswer(idx, droppedNote || symbol.label, answerTimeSeconds, isSymbolCorrect);
                }}
                width={400}
                height={150}
                highlightedKey={quiz.highlightedKey}
              />
            </div>
            <div style={{ marginTop: '20px' }}>
              <DraggableSymbols disabled={isAnswered} />
            </div>
          </div>
        );

      case 'IMAGE_CHOICE':
        // Question with image and multiple choice options
        return (
          <div>
            {/* Display question image if available */}
            {quiz.imgURL && (
              <div style={{ marginBottom: 20, textAlign: 'center' }}>
                <img 
                  src={quiz.imgURL} 
                  alt="Question" 
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
            )}
            
            {/* Display image options in a column layout */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
              {quiz.questionOptions && quiz.questionOptions.map((option, optIdx) => {
                // Determine if this option is selected and if it's correct
                const isSelected = selectedNote === option.id.toString();
                const isOptionCorrect = option.isCorrect;
                
                let border = "none";
                let boxShadow = "0 1px 4px #0001";
                let icon = null;
                
                if (showFeedback && isSelected) {
                  if (isOptionCorrect) {
                    border = "2px solid #1ca15f";
                    boxShadow = "0 0 0 2px #1ca15f33";
                    icon = <img src={checkIcon} alt="Check Icon" style={{ marginLeft: 200, width: 22, height: 22, display: 'flex', alignItems: 'center' }} />;
                  } else {
                    border = "2px solid #d32f2f";
                    boxShadow = "0 0 0 2px #d32f2f33";
                    icon = <img src={crossIcon} alt="Cross Icon" style={{ marginLeft: 200, width: 22, height: 22, display: 'flex', alignItems: 'center' }} />;
                  }
                }
                
                // Show correct answer with green border even if wrong option was selected
                if (showFeedback && isOptionCorrect && !isSelected) {
                  border = "2px solid #1ca15f";
                  boxShadow = "0 0 0 2px #1ca15f33";
                  icon = <img src={checkIcon} alt="Check Icon" style={{ marginLeft: 200, width: 22, height: 22, display: 'flex', alignItems: 'center' }} />;
                }
                
                return (
                  <button
                    key={option.id}
                    onClick={() => {
                      if (!isAnswered) {
                        setSelectedNote(option.id.toString());
                        setShowFeedback(true);
                        setIsAnswered(true);

                        const isAnswerCorrect = option.isCorrect;
                        setIsCorrect(isAnswerCorrect);

                        const answerTimeSeconds = Math.round((Date.now() - startTime) / 1000);

                        if (isAnswerCorrect) {
                          setResultMessage('✅ Correct!');
                          onCorrectAnswer(idx, option.id.toString(), answerTimeSeconds, isAnswerCorrect);
                        } else {
                          const correctOption = quiz.questionOptions.find(opt => opt.isCorrect);
                          setResultMessage(`❌ Selected option is wrong!`);
                          onCorrectAnswer(idx, option.id.toString(), answerTimeSeconds, isAnswerCorrect);
                        }
                      }
                    }}
                    disabled={isAnswered}
                    style={{
                      width: 500,
                      height: 40,
                      padding: 12,
                      background: "rgb(224, 237, 254)",
                      color: '#222',
                      fontWeight: 500,
                      fontSize: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: isAnswered ? "default" : "pointer",
                      outline: "none",
                      position: 'relative',
                      transition: 'border 0.2s',
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", position: "relative" }}>
                      <span style={{ position: "absolute", left: 12, fontWeight: 600, fontSize: 16 }}>
                        {String.fromCharCode(65 + optIdx)}.
                      </span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <img 
                          src={option.optionImagePath} 
                          alt={option.optionText || `Option ${optIdx + 1}`}
                          style={{
                            maxWidth: "120px",
                            maxHeight: "80px",
                            objectFit: "contain",
                            border: showFeedback && isSelected ? (isOptionCorrect ? "3px solid #1ca15f" : "3px solid #d32f2f") : "none",
                            borderRadius: "4px",
                            padding: "2px"
                          }}
                        />
                        {showFeedback && isSelected && (
                          <img 
                            src={isOptionCorrect ? checkIcon : crossIcon} 
                            alt={isOptionCorrect ? "Correct" : "Incorrect"} 
                            style={{ 
                              width: 20, 
                              height: 20,
                              marginLeft: 8
                            }} 
                          />
                        )}
                        {showFeedback && isOptionCorrect && !isSelected && (
                          <img 
                            src={checkIcon} 
                            alt="Correct" 
                            style={{ 
                              width: 20, 
                              height: 20,
                              marginLeft: 8
                            }} 
                          />
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
        
      default:
        return <p>Unsupported question type: {quiz.questionType}</p>;
    }
  };
  
  return (
    <div>
      <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: 20, lineHeight: '100%', letterSpacing: 0, marginBottom: 15, color: '#000000', textAlign: 'left' }}>
        {idx + 1}. {quiz.questionText}
      </div>
      
      <div style={{ margin: '20px 0' }}>
        {renderQuestionContent()}
      </div>
      
      {isAnswered && (
        <div className={`result-message ${isCorrect ? 'correct' : 'incorrect'}`}>
          {resultMessage}
        </div>
      )}
    </div>
  );
};

// Test function to demonstrate dynamic piano range
export const testDynamicPianoRange = () => {
  const testQuestions = [
    {
      id: 1,
      questionText: "Play the note C4 on the piano",
      questionType: "TEXT_NOTE_TO_PIANO",
      pianoFromNote: "C4",
      pianoToNote: "G4",
      correctAnswer: "C4"
    },
    {
      id: 2,
      questionText: "Which note is highlighted on the piano?",
      questionType: "PIANO_TO_TEXT_NOTE",
      pianoFromNote: "D3",
      pianoToNote: "A4",
      highlightedKey: "G3",
      correctAnswer: "G3"
    },
    {
      id: 3,
      questionText: "Play the symbol note 🎵 on the piano",
      questionType: "SYMBOL_NOTE_TO_PIANO",
      pianoFromNote: "E4",
      pianoToNote: "C5",
      correctAnswer: "A4"
    },
    {
      id: 4,
      questionText: "Drag the correct symbol for this highlighted key",
      questionType: "PIANO_TO_SYMBOL_NOTE",
      pianoFromNote: "F3",
      pianoToNote: "B4",
      highlightedKey: "D4",
      correctAnswer: "D4"
    }
  ];
  
  return testQuestions;
};

export default MusicQuizCard;